import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export interface FacilityOption {
  facility_id: string
  facility_name: string
  facility_type: string | null
  address: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  license_number: string | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  employee_count: number | null
  specialties: string[]
  notes: string | null
  accreditation_framework: string
}

export interface FacilityInput {
  facility_name: string
  state: string
  facility_type?: string
  address?: string
  city?: string
  zip_code?: string
  license_number?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  employee_count?: number | null
  specialties?: string[] | string
  notes?: string
  accreditation_framework?: string
}

export interface OrganizationProfile {
  tenant_id: string
  name: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  address: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  employee_count: number | null
  notes: string | null
}

export interface FacilityFeatureFlag {
  flag_id: string
  tenant_id: string
  facility_id: string
  feature_key: string
  enabled: boolean
  updated_at: string
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parseAccreditationFramework(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : 'cms'
}

export async function getFacilities(): Promise<FacilityOption[]> {
  const { data, error } = await invokeFunction<{ facilities: unknown }>('facilities', {
    method: 'GET',
    path: '/',
  })

  if (error) throw toApiRequestError(error, 'Failed to load facilities')
  if (!data || !Array.isArray(data.facilities)) throw new Error('Malformed facilities response from server')

  return data.facilities
    .map((row) => {
      if (!asRecord(row)) return null
      if (typeof row.facility_id !== 'string') return null
      if (typeof row.facility_name !== 'string') return null
      return {
        facility_id: row.facility_id,
        facility_name: row.facility_name,
        facility_type: typeof row.facility_type === 'string' ? row.facility_type : null,
        address: typeof row.address === 'string' ? row.address : null,
        city: typeof row.city === 'string' ? row.city : null,
        state: typeof row.state === 'string' ? row.state : null,
        zip_code: typeof row.zip_code === 'string' ? row.zip_code : null,
        license_number: typeof row.license_number === 'string' ? row.license_number : null,
        contact_name: typeof row.contact_name === 'string' ? row.contact_name : null,
        contact_email: typeof row.contact_email === 'string' ? row.contact_email : null,
        contact_phone: typeof row.contact_phone === 'string' ? row.contact_phone : null,
        employee_count: typeof row.employee_count === 'number' ? row.employee_count : null,
        specialties: Array.isArray(row.specialties)
          ? row.specialties.filter((entry): entry is string => typeof entry === 'string')
          : [],
        notes: typeof row.notes === 'string' ? row.notes : null,
        accreditation_framework: parseAccreditationFramework(row.accreditation_framework),
      }
    })
    .filter((row): row is FacilityOption => row !== null)
}

export async function createFacility(input: FacilityInput): Promise<FacilityOption> {
  const { data, error } = await invokeFunction<unknown>('facilities', {
    method: 'POST',
    path: '/',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to create facility')
  if (!asRecord(data)) throw new Error('Malformed facility response from server')
  const rows = [data]
    .map((row) => {
      if (!asRecord(row)) return null
      if (typeof row.facility_id !== 'string' || typeof row.facility_name !== 'string') return null
      return {
        facility_id: row.facility_id,
        facility_name: row.facility_name,
        facility_type: typeof row.facility_type === 'string' ? row.facility_type : null,
        address: typeof row.address === 'string' ? row.address : null,
        city: typeof row.city === 'string' ? row.city : null,
        state: typeof row.state === 'string' ? row.state : null,
        zip_code: typeof row.zip_code === 'string' ? row.zip_code : null,
        license_number: typeof row.license_number === 'string' ? row.license_number : null,
        contact_name: typeof row.contact_name === 'string' ? row.contact_name : null,
        contact_email: typeof row.contact_email === 'string' ? row.contact_email : null,
        contact_phone: typeof row.contact_phone === 'string' ? row.contact_phone : null,
        employee_count: typeof row.employee_count === 'number' ? row.employee_count : null,
        specialties: Array.isArray(row.specialties)
          ? row.specialties.filter((entry): entry is string => typeof entry === 'string')
          : [],
        notes: typeof row.notes === 'string' ? row.notes : null,
        accreditation_framework: parseAccreditationFramework(row.accreditation_framework),
      }
    })
    .filter((row): row is FacilityOption => row !== null)
  if (rows.length === 0) throw new Error('Malformed facility response from server')
  return rows[0]
}

export async function updateFacility(facilityId: string, input: Partial<FacilityInput>): Promise<FacilityOption> {
  const { data, error } = await invokeFunction<unknown>('facilities', {
    method: 'PATCH',
    path: `/${facilityId}`,
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to update facility')
  if (!asRecord(data)) throw new Error('Malformed facility response from server')
  return createFacilityMapper(data)
}

function createFacilityMapper(row: Record<string, unknown>): FacilityOption {
  if (typeof row.facility_id !== 'string' || typeof row.facility_name !== 'string') {
    throw new Error('Malformed facility response from server')
  }
  return {
    facility_id: row.facility_id,
    facility_name: row.facility_name,
    facility_type: typeof row.facility_type === 'string' ? row.facility_type : null,
    address: typeof row.address === 'string' ? row.address : null,
    city: typeof row.city === 'string' ? row.city : null,
    state: typeof row.state === 'string' ? row.state : null,
    zip_code: typeof row.zip_code === 'string' ? row.zip_code : null,
    license_number: typeof row.license_number === 'string' ? row.license_number : null,
    contact_name: typeof row.contact_name === 'string' ? row.contact_name : null,
    contact_email: typeof row.contact_email === 'string' ? row.contact_email : null,
    contact_phone: typeof row.contact_phone === 'string' ? row.contact_phone : null,
    employee_count: typeof row.employee_count === 'number' ? row.employee_count : null,
    specialties: Array.isArray(row.specialties)
      ? row.specialties.filter((entry): entry is string => typeof entry === 'string')
      : [],
    notes: typeof row.notes === 'string' ? row.notes : null,
    accreditation_framework: parseAccreditationFramework(row.accreditation_framework),
  }
}

export async function getFacilityById(id: string): Promise<FacilityOption> {
  const { data, error } = await invokeFunction<unknown>('facilities', {
    method: 'GET',
    path: `/${id}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load facility')
  if (!asRecord(data)) throw new Error('Malformed facility response')
  return createFacilityMapper(data)
}

export async function getOrganizationProfile(): Promise<OrganizationProfile> {
  const { data, error } = await invokeFunction<unknown>('facilities', {
    method: 'GET',
    path: '/organization-profile',
  })
  if (error) throw toApiRequestError(error, 'Failed to load organization profile')
  if (!asRecord(data)) throw new Error('Malformed organization response from server')
  if (typeof data.tenant_id !== 'string' || typeof data.name !== 'string') {
    throw new Error('Malformed organization response from server')
  }
  return {
    tenant_id: data.tenant_id,
    name: data.name,
    contact_name: typeof data.contact_name === 'string' ? data.contact_name : null,
    contact_email: typeof data.contact_email === 'string' ? data.contact_email : null,
    contact_phone: typeof data.contact_phone === 'string' ? data.contact_phone : null,
    address: typeof data.address === 'string' ? data.address : null,
    city: typeof data.city === 'string' ? data.city : null,
    state: typeof data.state === 'string' ? data.state : null,
    zip_code: typeof data.zip_code === 'string' ? data.zip_code : null,
    employee_count: typeof data.employee_count === 'number' ? data.employee_count : null,
    notes: typeof data.notes === 'string' ? data.notes : null,
  }
}

export async function updateOrganizationProfile(input: Partial<OrganizationProfile>): Promise<OrganizationProfile> {
  const { data, error } = await invokeFunction<unknown>('facilities', {
    method: 'PATCH',
    path: '/organization-profile',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to update organization profile')
  if (!asRecord(data)) throw new Error('Malformed organization response from server')
  if (typeof data.tenant_id !== 'string' || typeof data.name !== 'string') {
    throw new Error('Malformed organization response from server')
  }
  return {
    tenant_id: data.tenant_id,
    name: data.name,
    contact_name: typeof data.contact_name === 'string' ? data.contact_name : null,
    contact_email: typeof data.contact_email === 'string' ? data.contact_email : null,
    contact_phone: typeof data.contact_phone === 'string' ? data.contact_phone : null,
    address: typeof data.address === 'string' ? data.address : null,
    city: typeof data.city === 'string' ? data.city : null,
    state: typeof data.state === 'string' ? data.state : null,
    zip_code: typeof data.zip_code === 'string' ? data.zip_code : null,
    employee_count: typeof data.employee_count === 'number' ? data.employee_count : null,
    notes: typeof data.notes === 'string' ? data.notes : null,
  }
}

export async function getFacilityFeatureFlags(facilityId?: string): Promise<FacilityFeatureFlag[]> {
  const query = facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ''
  const { data, error } = await invokeFunction<{ flags: unknown }>('facilities', {
    method: 'GET',
    path: `/facility-feature-flags${query}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load facility feature flags')
  if (!data || !Array.isArray(data.flags)) throw new Error('Malformed feature flags response from server')
  return data.flags
    .map((row) => {
      if (!asRecord(row)) return null
      if (typeof row.flag_id !== 'string') return null
      if (typeof row.tenant_id !== 'string') return null
      if (typeof row.facility_id !== 'string') return null
      if (typeof row.feature_key !== 'string') return null
      return {
        flag_id: row.flag_id,
        tenant_id: row.tenant_id,
        facility_id: row.facility_id,
        feature_key: row.feature_key,
        enabled: row.enabled === true,
        updated_at: typeof row.updated_at === 'string' ? row.updated_at : new Date(0).toISOString(),
      }
    })
    .filter((row): row is FacilityFeatureFlag => row !== null)
}

export async function updateFacilityFeatureFlag(
  facilityId: string,
  featureKey: string,
  enabled: boolean,
): Promise<FacilityFeatureFlag> {
  const { data, error } = await invokeFunction<unknown>('facilities', {
    method: 'PUT',
    path: `/facility-feature-flags/${facilityId}`,
    body: { feature_key: featureKey, enabled },
  })
  if (error) throw toApiRequestError(error, 'Failed to update facility feature flag')
  if (!asRecord(data)) throw new Error('Malformed facility feature flag response from server')
  if (
    typeof data.flag_id !== 'string' ||
    typeof data.tenant_id !== 'string' ||
    typeof data.facility_id !== 'string' ||
    typeof data.feature_key !== 'string'
  ) {
    throw new Error('Malformed facility feature flag response from server')
  }
  return {
    flag_id: data.flag_id,
    tenant_id: data.tenant_id,
    facility_id: data.facility_id,
    feature_key: data.feature_key,
    enabled: data.enabled === true,
    updated_at: typeof data.updated_at === 'string' ? data.updated_at : new Date(0).toISOString(),
  }
}
